import { FTUE_FLOW_PRIORITY, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import type { FtuePlacement } from '@/ftue'
import { useCallback, useEffect } from 'react'

export const RESUME_BUILDER_JOURNEY_FLOW = 'resume-builder-journey'
export const RESUME_BUILDER_ELIGIBILITY_FLOW = 'resume-builder-eligibility'

export const MIN_MEANINGFUL_SUMMARY_LENGTH = 40

export function completesDirectEditFtue(path: string, value: string): boolean {
  return path === 'summary' && value.trim().length >= MIN_MEANINGFUL_SUMMARY_LENGTH
}

export function completesRenameTitleFtue(title: string): boolean {
  const normalized = title.trim()
  return normalized.length > 0 && normalized.toLowerCase() !== 'untitled resume'
}

export const RESUME_BUILDER_JOURNEY_STEPS = {
  addResume: 'resume-builder-add-first-resume',
  liveScore: 'resume-builder-watch-live-score',
  // Legacy ids stay exported while old persisted state and page call-sites are
  // migrated. They are no longer registered as proactive FTUE steps.
  buildResume: 'resume-builder-open-builder',
  startMethod: 'resume-builder-choose-start-method',
  renameTitle: 'resume-builder-rename-draft',
  directEdit: 'resume-builder-edit-resume-text',
  template: 'resume-builder-choose-template',
  score: 'resume-builder-review-score',
  chat: 'resume-builder-prompt-agent',
  drafts: 'resume-builder-open-drafts',
  continueToAgent: 'resume-builder-continue-job-agent',
} as const

const ANCHORS = {
  addResume: 'resume-builder-journey-add-resume',
  buildResume: 'resume-builder-journey-build-resume',
  startMethod: 'resume-builder-journey-start-method',
  renameTitle: 'resume-builder-journey-rename-title',
  directEdit: 'resume-builder-journey-direct-edit',
  template: 'resume-builder-journey-template',
  score: 'resume-builder-journey-score',
  chat: 'resume-builder-journey-chat',
  drafts: 'resume-builder-journey-drafts',
  continueToAgent: 'resume-builder-journey-continue',
} as const

/**
 * Anchor for the "add your first resume" section on /resume — the target of this
 * journey's first coachmark. Exported so other surfaces can point at the same
 * spot (the Job Agent "needs a resume" notice reuses it).
 */
export const ADD_RESUME_ANCHOR_ID = ANCHORS.addResume

const journeyStep = (
  id: string,
  anchorId: string,
  order: number,
  body: string,
  placement: FtuePlacement,
  options: {
    autoCompleteMs?: number
    allowAnchorInteraction?: boolean
    dismissLabel?: string
    dismissOnAction?: boolean
    hideDismiss?: boolean
    spotlight?: boolean
    primaryCta?: { label: string; action: 'next' | 'complete' | 'skip' }
    condition?: () => boolean
    overlayBody?: string
    overlayPlacement?: FtuePlacement
  } = {},
) => {
  const { dismissLabel = 'Ok', overlayBody, primaryCta, ...stepOptions } = options
  return {
    id,
    flow: RESUME_BUILDER_JOURNEY_FLOW,
    // A phase per milestone keeps the shared coachmark renderer in its
    // single-concept mode, so this journey renders a short contextual acknowledgement
    // instead of Next/Back/Skip controls.
    phase: id,
    order,
    trigger: { type: 'immediate' as const },
    anchorId,
    presentation: 'coachmark' as const,
    placement,
    hideSkip: true,
    content: {
      body,
      dismissLabel,
      ...(primaryCta ? { primaryCta } : {}),
      ...(overlayBody ? { overlayBody } : {}),
    },
    ...stepOptions,
  }
}

function registerJourney() {
  registerSteps([
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.addResume,
      ANCHORS.addResume,
      1,
      'Start your job search : upload an existing PDF or build a resume with Pelgo.',
      'bottom',
      {
        // No ack button: the two real ways forward (upload a PDF, or Build a
        // resume) both live inside the anchored card and complete the step
        // themselves, so a dismiss control would only offer a way to shrug off
        // the one choice this screen exists to make. `hideSkip` above already
        // drops Skip, leaving a pure pointer at the card; ESC still exits.
        hideDismiss: true,
        allowAnchorInteraction: true,
        condition: () => window.location.pathname === '/resume',
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.directEdit,
      ANCHORS.directEdit,
      2,
      'This is your live resume preview. You can co-edit any field directly and use the chat on the left to refine it',
      // Anchored to the whole resume sheet, so the bubble sits beside it near the
      // top rather than hunting one field -- with no spotlight there is nothing
      // to draw the eye to a single input anyway.
      'left-start',
      {
        dismissLabel: 'Ok',
        allowAnchorInteraction: true,
        // Non-blocking: the copy tells the user to type in the field AND to ask
        // the chat on the left to refine it. The chat panel sits outside this
        // step's anchor, so a scrim would swallow clicks on the very thing the
        // nudge asks for -- and its focus trap would block typing there too.
        spotlight: false,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.liveScore,
      ANCHORS.score,
      3,
      'Your score will update here real-time as you build your resume',
      'top',
      {
        // Non-blocking so the canvas stays live while the user reads it; Next is
        // the only control, and completing it hands off to the Job Agent step.
        spotlight: false,
        primaryCta: { label: 'Next', action: 'next' },
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
    journeyStep(
      RESUME_BUILDER_JOURNEY_STEPS.continueToAgent,
      ANCHORS.continueToAgent,
      4,
      'Whenever you’re ready, start matching jobs using this resume.',
      'bottom',
      {
        dismissLabel: 'Ok',
        dismissOnAction: true,
        // Non-blocking: this is a "when you're ready" pointer at the handoff, so
        // it must not scrim the canvas or trap focus -- the user keeps editing
        // the resume with the nudge visible and clicks through on their own time.
        spotlight: false,
        condition: () =>
          window.location.pathname === '/resume-builder' &&
          new URLSearchParams(window.location.search).has('draft'),
      },
    ),
  ])
}

/**
 * Register and resume the account-persisted first-resume journey. `eligible`
 * starts it only for a genuinely empty resume library; after its first milestone
 * is saved, any builder page can resume the remaining steps across reloads and
 * devices without accidentally onboarding established users.
 */
export function useResumeBuilderJourneyFtue(eligible = false, eligibilityPending = false) {
  const hasStarted = useFtueStore((state) => state.seen.has(RESUME_BUILDER_JOURNEY_STEPS.addResume))
  const hasReachedAgent = useFtueStore((state) =>
    state.seen.has(RESUME_BUILDER_JOURNEY_STEPS.continueToAgent),
  )
  useEffect(() => {
    registerJourney()
    const store = useFtueStore.getState()
    if (eligibilityPending) {
      store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
        holdWhenEmpty: true,
      })
      return
    }
    if (eligible || (hasStarted && !hasReachedAgent)) {
      store.startFlow(RESUME_BUILDER_JOURNEY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney)
    } else if (store.activeFlow === RESUME_BUILDER_ELIGIBILITY_FLOW) {
      store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
        holdWhenEmpty: false,
      })
    }
  }, [eligibilityPending, eligible, hasReachedAgent, hasStarted])

  useEffect(
    () => () => {
      const store = useFtueStore.getState()
      if (store.activeFlow === RESUME_BUILDER_ELIGIBILITY_FLOW) {
        store.startFlow(RESUME_BUILDER_ELIGIBILITY_FLOW, FTUE_FLOW_PRIORITY.primaryJourney, {
          holdWhenEmpty: false,
        })
      }
    },
    [],
  )

  const complete = useCallback((stepId: string) => {
    const state = useFtueStore.getState()
    if (!state.seen.has(stepId)) state.complete(stepId)
  }, [])

  return {
    started: hasStarted,
    anchors: {
      addResume: useFtueAnchor(ANCHORS.addResume),
      buildResume: useFtueAnchor(ANCHORS.buildResume),
      startMethod: useFtueAnchor(ANCHORS.startMethod),
      renameTitle: useFtueAnchor(ANCHORS.renameTitle),
      directEdit: useFtueAnchor(ANCHORS.directEdit),
      template: useFtueAnchor(ANCHORS.template),
      templateMenu: useFtueAnchor(ANCHORS.template, 'overlay'),
      score: useFtueAnchor(ANCHORS.score),
      chat: useFtueAnchor(ANCHORS.chat),
      drafts: useFtueAnchor(ANCHORS.drafts),
      continueToAgent: useFtueAnchor(ANCHORS.continueToAgent),
    },
    complete,
  }
}
